import React, { useState, useEffect, useCallback } from 'react';
import { Send, Search, Tag, Users, Image, Upload, CheckCircle, AlertCircle, X, RotateCw, ChevronDown, ChevronUp, User, Plus, Calendar } from 'lucide-react';
import apiFetch from '../../utils/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractVars(components = []) {
    const vars = new Set();
    components.forEach(c => {
        const text = c.text || '';
        (text.match(/\{\{(\w+)\}\}/g) || []).forEach(m => vars.add(m.replace(/\{\{|\}\}/g, '')));
    });
    return [...vars];
}
function getBodyText(components = []) {
    return components.find(c => c.type === 'BODY')?.text || '';
}
function hasImageHeader(components = []) {
    const h = components.find(c => c.type === 'HEADER');
    return h?.format === 'IMAGE';
}
function applyVars(text, vars) {
    let result = text;
    Object.entries(vars).forEach(([k, v]) => {
        result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v || `{{${k}}}`);
    });
    return result;
}
function getHeaderText(components = []) {
    return components.find(c => c.type === 'HEADER' && c.format === 'TEXT')?.text || '';
}
function getFooterText(components = []) {
    return components.find(c => c.type === 'FOOTER')?.text || '';
}
function getButtons(components = []) {
    return components.find(c => c.type === 'BUTTONS')?.buttons || [];
}

// ─── Template Selector Dropdown ───────────────────────────────────────────────
const TemplatePicker = ({ templates, loading, selected, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const filtered = templates.filter(t => t.status === 'APPROVED' && (!q || t.name.toLowerCase().includes(q)));

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14, fontWeight: selected ? 700 : 400, color: selected ? '#065f46' : '#6b7280', textAlign: 'left' }}
            >
                <span>{loading ? 'Cargando plantillas...' : selected ? `📋 ${selected.name}` : 'Selecciona una plantilla aprobada...'}</span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', overflow: 'hidden', marginTop: 4 }}>
                    <div style={{ padding: 10, borderBottom: '1px solid #f3f4f6' }}>
                        <input value={q} onChange={e => setQ(e.target.value.toLowerCase())} placeholder="Buscar plantilla..." autoFocus style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Sin plantillas aprobadas</div>
                        ) : filtered.map(t => (
                            <button key={t.id || t.name} onClick={() => { onSelect(t); setOpen(false); setQ(''); }}
                                style={{ width: '100%', padding: '12px 16px', background: selected?.name === t.name ? '#f0fdf4' : 'white', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f9fafb' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                onMouseLeave={e => e.currentTarget.style.background = selected?.name === t.name ? '#f0fdf4' : 'white'}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{t.name}</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>{t.language} · {t.category}</div>
                                </div>
                                {selected?.name === t.name && <CheckCircle size={16} color="#25d366" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Contact Panel (right side) ───────────────────────────────────────────────
const ContactPanel = ({ 
    tags, 
    conversations, 
    selectionMode, 
    setSelectionMode, 
    selectedTagId, 
    setSelectedTagId, 
    selectedPhones, 
    setSelectedPhones, 
    searchContact, 
    setSearchContact,
    importedPhones = [],
    setImportedPhones,
    importInput = '',
    setImportInput,
    startDate, setStartDate, endDate, setEndDate, datePreview
}) => {
    const [searchTag, setSearchTag] = useState('');

    const filteredTags = (tags || []).filter(t => 
        !searchTag || t.name.toLowerCase().includes(searchTag.toLowerCase())
    );

    const filtered = (conversations || []).filter(c => {
        const q = searchContact.toLowerCase();
        return !q || c.contact?.name?.toLowerCase().includes(q) || (c.contact?.phone || '').includes(q);
    });

    const togglePhone = phone => setSelectedPhones(prev => prev.includes(phone) ? prev.filter(p => p !== phone) : [...prev, phone]);

    return (
        <div className="wa-bulk-contact-content">
            {/* Recipient selection: keep every option visible in the narrow panel. */}
            <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>¿A quién quieres enviar?</div>
                <div className="wa-bulk-selection-modes">
                {[
                    ['manual', <User size={14} />, 'Contactos'],
                    ['import', <Plus size={14} />, 'Nuevo/Pegar'],
                    ['tag', <Tag size={14} />, 'Etiqueta'],
                    ['date', <Calendar size={14} />, 'Por fecha'],
                    ['all', <Users size={14} />, 'Todos']
                ].map(([mode, icon, label]) => (
                    <button 
                        key={mode} 
                        type="button"
                        aria-pressed={selectionMode === mode}
                        onClick={() => setSelectionMode(mode)} 
                        style={{ 
                            minWidth: 0,
                            padding: '12px 10px',
                            borderRadius: 8,
                            border: selectionMode === mode ? '2px solid #059669' : '2px solid #e5e7eb',
                            background: selectionMode === mode ? '#f0fdf4' : 'white', 
                            color: selectionMode === mode ? '#059669' : '#6b7280', 
                            fontWeight: selectionMode === mode ? 700 : 500, 
                            fontSize: 13,
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 8,
                            textAlign: 'left'
                        }}
                    >
                        {icon}
                        <span>{label}</span>
                    </button>
                ))}
                </div>
            </div>

            {selectionMode === 'date' && (
                <div style={{ padding: 16, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
                    <strong>Fecha del último mensaje</strong>
                    <span>Conversaciones activas de esta sede. Incluye ambos días completos, hora de Colombia.</span>
                    <label htmlFor="wa-bulk-start-date">Desde</label>
                    <input id="wa-bulk-start-date" type="date" value={startDate} max={endDate || undefined} onChange={e => setStartDate(e.target.value)} style={{ padding: 12, border: '1px solid #d1d5db', borderRadius: 8, font: 'inherit', minWidth: 0 }} />
                    <label htmlFor="wa-bulk-end-date">Hasta</label>
                    <input id="wa-bulk-end-date" type="date" value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} style={{ padding: 12, border: '1px solid #d1d5db', borderRadius: 8, font: 'inherit', minWidth: 0 }} />
                    {(!startDate || !endDate) ? <span>Selecciona ambas fechas.</span>
                        : startDate > endDate ? <span role="alert">La fecha inicial debe ser anterior o igual a la final.</span>
                        : datePreview?.key !== `${startDate}/${endDate}` ? <span>Consultando destinatarios...</span>
                        : datePreview.error ? <span role="alert">{datePreview.error}</span>
                        : <strong aria-live="polite">{datePreview.total} conversaciones para enviar</strong>}
                </div>
            )}

            {/* Tag selector */}
            {selectionMode === 'tag' && (
                <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', flex: 1, overflowY: 'auto' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Selecciona una etiqueta</div>
                    <div style={{ position: 'relative', marginBottom: 12 }}>
                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input 
                            value={searchTag} 
                            onChange={e => setSearchTag(e.target.value)} 
                            placeholder="Buscar etiqueta..." 
                            style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} 
                        />
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {filteredTags.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#6b7280', padding: '10px 0' }}>No se encontraron etiquetas</div>
                        ) : filteredTags.map(t => (
                            <button key={t.id} onClick={() => setSelectedTagId(t.id === selectedTagId ? null : t.id)}
                                style={{ padding: '4px 10px', borderRadius: 999, border: t.id === selectedTagId ? '2px solid #059669' : '1px solid #e5e7eb', background: t.id === selectedTagId ? '#f0fdf4' : 'white', color: t.id === selectedTagId ? '#059669' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                            >
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color || '#6b7280', display: 'inline-block' }} />
                                {t.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* All mode message */}
            {selectionMode === 'all' && (
                <div style={{ padding: 16, background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 13, color: '#92400e', flexShrink: 0 }}>
                    ⚠️ Se enviará a <strong>todos los contactos activos</strong> de esta sede.
                </div>
            )}

            {/* Search (manual mode) */}
            {selectionMode === 'manual' && (
                <div style={{ padding: 10, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input value={searchContact} onChange={e => setSearchContact(e.target.value)} placeholder="Buscar por nombre o teléfono..." style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                </div>
            )}

            {/* Contact list */}
            {selectionMode === 'manual' && (
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                            <Users size={32} style={{ margin: '0 auto 8px', opacity: 0.3, display: 'block' }} />
                            No se encontraron contactos
                        </div>
                    ) : filtered.map(c => {
                        const phone = c.contact?.phone || c.phone;
                        const name = c.contact?.name || c.contact_name || phone;
                        const isSelected = selectedPhones.includes(phone);
                        return (
                            <div key={phone} onClick={() => togglePhone(phone)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f9fafb', cursor: 'pointer', background: isSelected ? '#f0fdf4' : 'white' }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                            >
                                <div style={{ width: 18, height: 18, borderRadius: 4, border: isSelected ? 'none' : '2px solid #d1d5db', background: isSelected ? '#25d366' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {isSelected && <CheckCircle size={14} color="white" />}
                                </div>
                                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#25d366,#128c7e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                                    {(name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>{phone}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Import / Paste list mode */}
            {selectionMode === 'import' && (
                <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Plus size={14} color="#25d366" /> Nuevo Contacto / Pegar Lista
                    </div>
                    
                    <p style={{ fontSize: 11, color: '#6b7280', margin: 0, lineHeight: 1.4 }}>
                        Ingresa un número para iniciar un chat o pega varios separados por comas, espacios o saltos de línea (ej: Excel).
                    </p>

                    <textarea
                        value={importInput}
                        onChange={(e) => {
                            const val = e.target.value;
                            setImportInput(val);
                            
                            const parsed = [];
                            const rawParts = val.split(/[,;\n\r\t]+/);
                            rawParts.forEach(part => {
                                const trimmed = part.trim();
                                if (!trimmed) return;
                                
                                const spaceParts = trimmed.split(/\s+/);
                                if (spaceParts.length > 1) {
                                    const isMultipleNumbers = spaceParts.every(sp => sp.replace(/\D/g, '').length >= 10);
                                    if (isMultipleNumbers) {
                                        spaceParts.forEach(sp => {
                                            let cleaned = sp.replace(/\D/g, '');
                                            if (cleaned.length === 10 && cleaned.startsWith('3')) {
                                                cleaned = '57' + cleaned;
                                            }
                                            if (cleaned.length >= 7 && cleaned.length <= 15) {
                                                parsed.push(cleaned);
                                            }
                                        });
                                        return;
                                    }
                                }
                                
                                let cleaned = trimmed.replace(/\D/g, '');
                                if (cleaned.length === 10 && cleaned.startsWith('3')) {
                                    cleaned = '57' + cleaned;
                                }
                                if (cleaned.length >= 7 && cleaned.length <= 15) {
                                    parsed.push(cleaned);
                                }
                            });
                            
                            setImportedPhones([...new Set(parsed)]);
                        }}
                        placeholder="Ej: +57 315 123 4567&#10;573001234567, 573019876543"
                        style={{
                            width: '100%',
                            height: 120,
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 13,
                            outline: 'none',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                            fontFamily: 'monospace'
                        }}
                    />

                    {importedPhones.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{importedPhones.length} número(s) detectado(s)</span>
                                <button 
                                    onClick={() => { setImportedPhones([]); setImportInput(''); }} 
                                    style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: 0 }}
                                >
                                    Limpiar
                                </button>
                            </div>
                            
                            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f8fafc', padding: '4px 8px' }}>
                                {importedPhones.map((phone, idx) => (
                                    <div key={phone} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: idx === importedPhones.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{phone}</span>
                                        <button 
                                            onClick={() => {
                                                const filtered = importedPhones.filter(p => p !== phone);
                                                setImportedPhones(filtered);
                                                setImportInput(filtered.join(', '));
                                            }} 
                                            style={{ border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Footer count */}
            <div style={{ padding: '10px 14px', borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280', flexShrink: 0, background: '#fafafa' }}>
                {selectionMode === 'manual' && `${selectedPhones.length} seleccionado${selectedPhones.length !== 1 ? 's' : ''} de ${filtered.length}`}
                {selectionMode === 'import' && `${importedPhones.length} número${importedPhones.length !== 1 ? 's' : ''} para enviar`}
                {selectionMode === 'tag' && selectedTagId && `Etiqueta seleccionada`}
                {selectionMode === 'all' && `Todos los contactos`}
                {selectionMode === 'date' && datePreview?.key === `${startDate}/${endDate}` && !datePreview.error && `${datePreview.total} conversaciones en el rango`}
            </div>
        </div>
    );
};

// ─── Main WaBulkOfficial Component ────────────────────────────────────────────
const WaBulkOfficial = ({ conversations, tags }) => {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState({});
    const [headerImageUrl, setHeaderImageUrl] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [selectionMode, setSelectionMode] = useState('manual');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [datePreview, setDatePreview] = useState(null);

    useEffect(() => {
        if (selectionMode !== 'date' || !startDate || !endDate || startDate > endDate) return;
        let cancelled = false;
        const key = `${startDate}/${endDate}`;
        setDatePreview(null);
        apiFetch(`/api/wa-templates/date-recipients-count?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`)
            .then(async res => {
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'No se pudo consultar los destinatarios');
                if (!cancelled) setDatePreview({ key, total: data.total });
            })
            .catch(error => { if (!cancelled) setDatePreview({ key, error: error.message }); });
        return () => { cancelled = true; };
    }, [selectionMode, startDate, endDate]);
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [selectedPhones, setSelectedPhones] = useState([]);
    const [importedPhones, setImportedPhones] = useState([]);
    const [importInput, setImportInput] = useState('');
    const [searchContact, setSearchContact] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [stats, setStats] = useState(0);
    const [createCampaign, setCreateCampaign] = useState(false);
    const [campaignName, setCampaignName] = useState('');

    // Load templates and stats
    useEffect(() => {
        setLoadingTemplates(true);
        Promise.all([
            apiFetch('/api/wa-templates').then(r => r.json()).catch(() => ({})),
            apiFetch('/api/wa-templates/stats').then(r => r.json()).catch(() => ({}))
        ]).then(([templatesData, statsData]) => {
            setTemplates(templatesData.templates || []);
            setStats(statsData.currentMonthSent || 0);
        }).finally(() => setLoadingTemplates(false));
    }, []);

    // When template changes, reset variables and load its header image URL if available
    useEffect(() => {
        if (!selectedTemplate) {
            setVariables({});
            setHeaderImageUrl('');
            return;
        }
        const vars = extractVars(selectedTemplate.components || []);
        setVariables(Object.fromEntries(vars.map(v => [v, ''])));
        setHeaderImageUrl(selectedTemplate.headerImageUrl || '');
        setCampaignName(selectedTemplate.name);
    }, [selectedTemplate]);

    // Handle image upload
    const handleImageFile = async (file) => {
        setUploadingImage(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('folder', 'bulk');
            const res = await apiFetch('/api/upload', { method: 'POST', body: fd });
            const data = await res.json();
            if (data.file?.url) {
                setHeaderImageUrl(data.file.url);
            } else if (data.url) {
                setHeaderImageUrl(data.url);
            }
        } catch (e) {
            console.error('Upload error', e);
        } finally {
            setUploadingImage(false);
        }
    };

    const bodyText = selectedTemplate ? getBodyText(selectedTemplate.components || []) : '';
    const vars = selectedTemplate ? extractVars(selectedTemplate.components || []) : [];
    const needsImage = selectedTemplate ? hasImageHeader(selectedTemplate.components || []) : false;

    const canSend = selectedTemplate &&
        vars.every(v => variables[v]?.trim()) &&
        (!needsImage || headerImageUrl) &&
        (selectionMode === 'all' ||
         (selectionMode === 'date' && startDate && endDate && startDate <= endDate && datePreview?.key === `${startDate}/${endDate}` && datePreview.total > 0 && !datePreview.error) ||
         (selectionMode === 'tag' && selectedTagId) || 
         (selectionMode === 'manual' && selectedPhones.length > 0) ||
         (selectionMode === 'import' && importedPhones.length > 0)) &&
        !sending;

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        setResult(null);
        try {
            const res = await apiFetch('/api/wa-templates/bulk-send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    templateName: selectedTemplate.name,
                    templateLanguage: selectedTemplate.language,
                    templateText: bodyText,
                    variables,
                    headerImageUrl: headerImageUrl || null,
                    selectionMode,
                    startDate: selectionMode === 'date' ? startDate : undefined,
                    endDate: selectionMode === 'date' ? endDate : undefined,
                    tagId: selectionMode === 'tag' ? selectedTagId : null,
                    createCampaign,
                    campaignName: createCampaign ? (campaignName?.trim() || selectedTemplate?.name) : null,
                    recipients: selectionMode === 'manual' 
                        ? selectedPhones.map(p => ({ phone: p })) 
                        : selectionMode === 'import'
                        ? importedPhones.map(p => ({ phone: p }))
                        : []
                })
            });
            const data = await res.json();
            setResult(res.ok ? { success: true, ...data } : { success: false, error: data.error || 'Error desconocido' });
            if (res.ok) {
                setStats(prev => prev + (data.sent || 0));
            }
        } catch (e) {
            setResult({ success: false, error: e.message });
        } finally {
            setSending(false);
        }
    };

    const recipientSummary = selectionMode === 'manual'
        ? `${selectedPhones.length} contactos seleccionados`
        : selectionMode === 'import'
            ? `${importedPhones.length} números para enviar`
            : selectionMode === 'tag'
                ? (selectedTagId ? `Etiqueta: ${(tags || []).find(t => t.id === selectedTagId)?.name || selectedTagId}` : 'Selecciona una etiqueta')
                : selectionMode === 'date'
                    ? (datePreview?.key === `${startDate}/${endDate}` && !datePreview.error
                        ? `${datePreview.total} conversaciones · ${startDate} al ${endDate}`
                        : 'Selecciona un rango de fechas válido')
                    : 'Todos los contactos activos de esta sede';

    return (
        <div className="wa-bulk-page" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
            {/* Top bar */}
            <div style={{ padding: '18px 24px', background: 'white', borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>
                        <span style={{ color: '#25d366' }}>Envío Masivo</span> Oficial
                    </h1>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>Envía plantillas aprobadas de WhatsApp a tus contactos o etiquetas</p>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '8px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <div style={{ fontSize: 11, color: '#15803d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Enviados este mes</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#065f46', lineHeight: 1.2 }}>{stats.toLocaleString()}</div>
                </div>
            </div>

            {/* Main split layout */}
            <div className="wa-bulk-workspace">
                <section className="wa-bulk-template" aria-label="Plantilla y vista previa">
                    <div className="wa-bulk-section-title">1. Plantilla y vista previa</div>
                    <div className="wa-bulk-template-content">

                    {/* Result banner */}
                    {result && (
                        <div style={{ padding: '14px 18px', borderRadius: 10, background: result.success ? '#f0fdf4' : '#fee2e2', border: `1px solid ${result.success ? '#bbf7d0' : '#fecaca'}`, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                            {result.success ? <CheckCircle size={18} color="#15803d" /> : <AlertCircle size={18} color="#b91c1c" />}
                            <span style={{ color: result.success ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                                {result.success ? `✅ ${result.sent} enviados${result.failed > 0 ? ` | ❌ ${result.failed} fallidos` : ''}` : `Error: ${result.error}`}
                            </span>
                            <button onClick={() => setResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={16} /></button>
                        </div>
                    )}

                    {/* 1. Template picker */}
                    <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            📋 Plantilla WhatsApp
                            {selectedTemplate && <span style={{ marginLeft: 'auto', background: '#dcfce7', color: '#15803d', borderRadius: 999, padding: '2px 10px', fontSize: 11 }}>✓ Seleccionada</span>}
                        </div>
                        <TemplatePicker templates={templates} loading={loadingTemplates} selected={selectedTemplate} onSelect={setSelectedTemplate} />
                    </div>

                    {selectedTemplate && (
                        <div style={{ background: '#f5f3ff', borderRadius: 12, padding: 20, border: '2px solid #8b5cf6', boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.1), 0 2px 4px -1px rgba(139, 92, 246, 0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: createCampaign ? 12 : 0 }}>
                                <input 
                                    type="checkbox" 
                                    id="createCampaignCheck" 
                                    checked={createCampaign} 
                                    onChange={(e) => setCreateCampaign(e.target.checked)}
                                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#7c3aed' }}
                                />
                                <label htmlFor="createCampaignCheck" style={{ fontSize: 15, fontWeight: 800, color: '#4c1d95', cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 18 }}>📊</span> Crear campaña de seguimiento
                                </label>
                            </div>
                            {createCampaign && (
                                <div style={{ paddingLeft: 28, marginTop: 12 }}>
                                    <label style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6', display: 'block', marginBottom: 6 }}>Nombre de la campaña</label>
                                    <input 
                                        type="text" 
                                        value={campaignName} 
                                        onChange={(e) => setCampaignName(e.target.value)} 
                                        placeholder="Ej: Promo Agosto Vip"
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #c4b5fd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#4c1d95', fontWeight: 500 }}
                                    />
                                    <p style={{ fontSize: 12, color: '#6d28d9', margin: '8px 0 0' }}>Este es el nombre con el que verás los resultados de éxito o respuestas en el panel de métricas.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {selectedTemplate && (
                        <>
                            {/* 2. Variables automáticas */}
                            {vars.length > 0 && (
                                <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>✏️ Variables Personalizadas</div>
                                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Estas variables se aplicarán a todos los mensajes enviados.</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {vars.map(v => (
                                            <div key={v}>
                                                <label style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', display: 'block', marginBottom: 4, fontFamily: 'monospace' }}>{`{{${v}}}`}</label>
                                                <input
                                                    value={variables[v] || ''}
                                                    onChange={e => setVariables(prev => ({ ...prev, [v]: e.target.value }))}
                                                    placeholder={`Valor para {{${v}}}`}
                                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 3. Image upload (if required) */}
                            {needsImage && (
                                <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #fde68a' }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>🖼️ Esta plantilla requiere una imagen</div>
                                    <input
                                        value={headerImageUrl}
                                        onChange={e => setHeaderImageUrl(e.target.value)}
                                        placeholder="URL del archivo image (ej: https://example.com/archivo.jpg)"
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
                                    />
                                    <div style={{ textAlign: 'center', padding: '4px 0 8px', color: '#9ca3af', fontSize: 12 }}>— o —</div>
                                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px', borderRadius: 8, border: '2px dashed #e5e7eb', cursor: 'pointer', color: '#6b7280', fontSize: 13, fontWeight: 600, background: '#fafafa' }}>
                                        {uploadingImage ? <RotateCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={16} />}
                                        {uploadingImage ? 'Subiendo...' : 'Subir archivo desde mi equipo'}
                                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleImageFile(e.target.files[0]); }} />
                                    </label>
                                    {headerImageUrl && <div style={{ marginTop: 8, fontSize: 12, color: '#059669' }}>✅ Imagen: {headerImageUrl.substring(0, 60)}...</div>}
                                    <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>💡 La URL debe ser pública y accesible. Formatos soportados: JPG, PNG.</p>
                                </div>
                            )}

                            {/* 4. Preview */}
                            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                                    👁️ Vista previa en tiempo real <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>(reemplazo dinámico)</span>
                                </div>
                                <div style={{ 
                                     display: 'flex', 
                                     justifyContent: 'center', 
                                     alignItems: 'center', 
                                     background: '#f3f4f6', 
                                     padding: '24px', 
                                     borderRadius: 12,
                                     border: '1px solid #e5e7eb'
                                }}>
                                    <div style={{ 
                                         width: '100%', 
                                         maxWidth: '320px',
                                         backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\' viewBox=\'0 0 60 60\'%3E%3Cpath d=\'M0 0h30v30H0zm30 30h30v30H30z\' fill=\'%23ece5dd\' fill-opacity=\'.4\'/%3E%3C/svg%3E")',
                                         backgroundColor: '#efeae2',
                                         borderRadius: '16px',
                                         padding: '16px 12px',
                                         boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), inset 0 2px 4px 0 rgba(0,0,0,0.06)',
                                         display: 'flex',
                                         flexDirection: 'column',
                                         gap: '6px',
                                         boxSizing: 'border-box',
                                         border: '1px solid #d1d5db'
                                    }}>
                                        {/* WhatsApp Message Bubble */}
                                        <div style={{
                                             backgroundColor: 'white',
                                             borderRadius: '0px 10px 10px 10px',
                                             padding: '10px 12px',
                                             width: '100%',
                                             boxShadow: '0 1px 1.5px rgba(0,0,0,0.15)',
                                             boxSizing: 'border-box',
                                             position: 'relative'
                                        }}>
                                             {/* Header Text */}
                                             {getHeaderText(selectedTemplate.components || []) && (
                                                  <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#111827', marginBottom: '5px' }}>
                                                       {getHeaderText(selectedTemplate.components || [])}
                                                  </div>
                                             )}

                                             {/* Header Image */}
                                             {needsImage && headerImageUrl && (
                                                  <div style={{ marginBottom: '8px', borderRadius: '8px', overflow: 'hidden' }}>
                                                      <img src={headerImageUrl} alt="Header Preview" style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '140px', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                                                  </div>
                                             )}
                                             {needsImage && !headerImageUrl && (
                                                  <div style={{ marginBottom: '8px', borderRadius: '8px', backgroundColor: '#e5e7eb', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px', fontWeight: 600 }}>
                                                      🖼️ [Carga una imagen]
                                                  </div>
                                             )}

                                             {/* Body Text */}
                                             <div style={{
                                                  fontSize: '13.5px',
                                                  color: '#111827',
                                                  lineHeight: 1.45,
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word'
                                             }}>
                                                  {applyVars(bodyText, variables) || (
                                                       <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                                            Cuerpo del mensaje...
                                                       </span>
                                                  )}
                                             </div>

                                             {/* Footer */}
                                             {getFooterText(selectedTemplate.components || []) && (
                                                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px', borderTop: '1px solid #f3f4f6', paddingTop: '4px' }}>
                                                       {getFooterText(selectedTemplate.components || [])}
                                                  </div>
                                             )}

                                             {/* Time */}
                                             <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '9px', color: '#8b9396', marginTop: '4px' }}>
                                                  <span>12:00 PM</span>
                                             </div>
                                        </div>

                                        {/* Buttons */}
                                        {getButtons(selectedTemplate.components || []).length > 0 && (
                                             <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                  {getButtons(selectedTemplate.components || []).map((btn, idx) => (
                                                       <div 
                                                            key={idx} 
                                                            style={{
                                                                 backgroundColor: 'white',
                                                                 borderRadius: '8px',
                                                                 padding: '8px 10px',
                                                                 textAlign: 'center',
                                                                 fontSize: '13.5px',
                                                                 color: '#00a884',
                                                                 fontWeight: 600,
                                                                 boxShadow: '0 1px 1.5px rgba(0,0,0,0.1)',
                                                                 display: 'flex',
                                                                 alignItems: 'center',
                                                                 justifyContent: 'center',
                                                                 gap: '6px',
                                                                 border: '1px solid rgba(0,0,0,0.05)'
                                                            }}
                                                       >
                                                            <span>💬</span> {btn.text}
                                                       </div>
                                                  ))}
                                             </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    </div>
                </section>
                <section className="wa-bulk-recipients" aria-label="Destinatarios">
                    <div className="wa-bulk-section-title">
                        <Users size={18} color="#059669" /> 2. Destinatarios
                    </div>
                    <ContactPanel
                        tags={tags}
                        conversations={conversations}
                        selectionMode={selectionMode}
                        setSelectionMode={setSelectionMode}
                        selectedTagId={selectedTagId}
                        setSelectedTagId={setSelectedTagId}
                        selectedPhones={selectedPhones}
                        setSelectedPhones={setSelectedPhones}
                        searchContact={searchContact}
                        setSearchContact={setSearchContact}
                        importedPhones={importedPhones}
                        setImportedPhones={setImportedPhones}
                        importInput={importInput}
                        setImportInput={setImportInput}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        datePreview={datePreview}
                    />
                </section>
            </div>

            <div className="wa-bulk-send-bar">
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, overflowWrap: 'anywhere' }} aria-live="polite">
                    <strong style={{ fontSize: 14, color: '#111827' }}>Plantilla: {selectedTemplate?.name || 'Sin seleccionar'}</strong>
                    <span style={{ fontSize: 13, color: '#4b5563' }}>{recipientSummary}</span>
                </div>
                    <button
                        onClick={handleSend}
                        disabled={!canSend}
                        style={{ padding: '14px 24px', flexShrink: 0, borderRadius: 10, border: 'none', background: canSend ? 'linear-gradient(135deg,#25d366,#128c7e)' : '#94a3b8', color: 'white', fontSize: 15, fontWeight: 700, cursor: canSend ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                        {sending ? <><RotateCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> Enviando...</> : <><Send size={18} /> Enviar plantilla</>}
                    </button>
            </div>
            <style>{`
                .wa-bulk-workspace { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 65fr) minmax(0, 35fr); overflow: hidden; }
                .wa-bulk-recipients, .wa-bulk-template { min-width: 0; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
                .wa-bulk-recipients { background: white; border-left: 1px solid #e5e7eb; }
                .wa-bulk-section-title { padding: 16px 20px; font-size: 15px; font-weight: 700; color: #111827; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
                .wa-bulk-contact-content { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
                .wa-bulk-selection-modes { display: grid; grid-template-columns: repeat(auto-fit, minmax(115px, 1fr)); gap: 8px; }
                .wa-bulk-template-content { flex: 1; min-height: 0; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 16px; }
                .wa-bulk-template-content > * { flex-shrink: 0; }
                .wa-bulk-send-bar { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 24px; background: white; border-top: 1px solid #e5e7eb; box-shadow: 0 -4px 12px rgba(0,0,0,.04); }
                @media (max-width: 900px) {
                    .wa-bulk-workspace { display: flex; flex-direction: column; overflow-y: auto; }
                    .wa-bulk-recipients { min-height: 360px; max-height: 65vh; flex-shrink: 0; border-left: 0; border-top: 1px solid #e5e7eb; }
                    .wa-bulk-template { overflow: visible; flex-shrink: 0; }
                    .wa-bulk-template-content { overflow: visible; flex: none; }
                }
                @media (max-width: 520px) {
                    .wa-bulk-send-bar { align-items: stretch; flex-direction: column; gap: 10px; padding: 12px 16px; }
                    .wa-bulk-section-title { padding: 14px 16px; }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default WaBulkOfficial;
